import { supabase, TABLES, COLUMN_MAPS } from '../supabase';
import { Notification } from '../../types';

// ============================================
// Notifications Service
// ============================================

export const notificationsApi = {
  /**
   * Get notifications for a user
   */
  async getByUser(userId: string): Promise<Notification[]> {
    const { data, error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .select('*')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.notification.fromDb);
  },

  /**
   * Get unread notifications for a user
   */
  async getUnread(userId: string): Promise<Notification[]> {
    const { data, error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .select('*')
      .eq('user_id', userId)
      .eq('is_read', false)
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.notification.fromDb);
  },

  /**
   * Get unread count for a user
   */
  async getUnreadCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    
    if (error) throw error;
    return count || 0;
  },

  /**
   * Create a notification
   */
  async create(notification: Notification): Promise<void> {
    const dbData = COLUMN_MAPS.notification.toDb(notification);
    
    const { error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .insert(dbData);
    
    if (error) throw error;
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .update({ is_read: true })
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);
    
    if (error) throw error;
  },

  /**
   * Delete a notification
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Delete all read notifications for a user
   */
  async deleteRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .delete()
      .eq('user_id', userId)
      .eq('is_read', true);
    
    if (error) throw error;
  },

  /**
   * Delete all notifications for a user
   */
  async deleteAll(userId: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.NOTIFICATIONS)
      .delete()
      .eq('user_id', userId);
    
    if (error) throw error;
  },
};

export default notificationsApi;
